/**
 * Authentication Library - Generic auth functions
 *
 * This module provides centralized authentication functions that can be reused
 * across different projects. All auth logic should go through these functions.
 */

import { logout as logoutAction, setLoading } from './authSlice'
import { signInWithAzure, signOut } from '../lib/supabase'

/**
 * Centralized login function
 * Handles the complete login flow including state management and navigation
 */
export const performLogin = async ({ dispatch, onError = null }) => {
  try {
    // Set loading state
    dispatch(setLoading(true))

    // Sign in with Azure (Microsoft) using Supabase
    const { error } = await signInWithAzure()

    if (error) {
      throw new Error(error.message)
    }

    // Note: The actual login completion will be handled by the auth state listener
    // This function initiates the OAuth flow, and the user will be redirected
    // The session will be handled when they return from the OAuth provider

    return { success: true }
  } catch (error) {
    console.error('Login error:', error)
    const errorMessage = error instanceof Error ? error.message : 'Login failed'

    // Call error callback if provided
    if (onError) {
      onError(errorMessage)
    }

    return { success: false, error: errorMessage }
  } finally {
    // Clear loading state
    dispatch(setLoading(false))
  }
}

/**
 * Centralized logout function
 * Handles the complete logout flow including state cleanup and navigation
 * @param {{
 *   dispatch: import('../../state_manager/store.ts').AppDispatch,
 *   navigate: (to: string) => void,
 *   onSuccess?: (() => void) | null,
 *   onError?: ((error: unknown) => void) | null
 * }} args
 */
export const performLogout = async ({ dispatch, navigate, onSuccess = null, onError = null }) => {
  try {
    // Set loading state
    dispatch(setLoading(true))

    // Sign out from Supabase
    const { error } = await signOut()

    if (error) {
      throw new Error(error.message)
    }

    // Clear authentication state
    dispatch(logoutAction())

    // Navigate to login page
    navigate('/login')

    // Call success callback if provided
    if (onSuccess) {
      onSuccess()
    }

    return { success: true }
  } catch (error) {
    console.error('Logout error:', error)
    const errorMessage = error instanceof Error ? error.message : 'Logout failed'

    // Even if API call fails, clear local state and redirect
    dispatch(logoutAction())
    navigate('/login')

    // Call error callback if provided
    if (onError) {
      onError(errorMessage)
    }

    return { success: false, error: errorMessage }
  } finally {
    // Clear loading state
    dispatch(setLoading(false))
  }
}

/**
 * Get current user's access token
 * Utility function to get access token for API calls
 * @param {import('./authSlice.ts').AuthState} authState
 * @returns {string | null}
 */
export const getAccessToken = (authState) => {
  return authState.accessToken
}
